// src/context/IdleLogoutContext.tsx
//
// Provides pause/resume controls for the stylist idle-logout timer.
// Components that host potentially long operations (uploads, payment forms) call
// usePauseIdleTimer(key) to defer auto-logout while the operation is in progress.
//
// Design:
//   - pause requests are tracked by a string key in a Map<key, timestamp>
//   - the timer is paused when the Map is non-empty (reference-counting via keys)
//   - useEffect cleanup in usePauseIdleTimer enforces resume on unmount
//   - a 30-min hard cap force-releases any key that was never explicitly resumed
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { IDLE_PAUSE_HARD_CAP_MS } from '../constants/idleTimeout';

interface IdleLogoutContextValue {
  /** Register a pause request keyed by a unique string. Idempotent for the same key. */
  pauseTimer: (key: string) => void;
  /** Release a pause request. Timer resumes only when no keys remain. */
  resumeTimer: (key: string) => void;
  /** True when at least one pause key is active. */
  isPaused: boolean;
}

const IdleLogoutContext = createContext<IdleLogoutContextValue>({
  pauseTimer: () => {},
  resumeTimer: () => {},
  isPaused: false,
});

export const IdleLogoutProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Map<key, timestampWhenPauseRequested>
  const pauseMapRef = useRef<Map<string, number>>(new Map());
  const [isPaused, setIsPaused] = useState(false);

  const pauseTimer = useCallback((key: string) => {
    pauseMapRef.current.set(key, Date.now());
    setIsPaused(true);
  }, []);

  const resumeTimer = useCallback((key: string) => {
    pauseMapRef.current.delete(key);
    setIsPaused(pauseMapRef.current.size > 0);
  }, []);

  // Hard cap: evict any key that has been paused longer than IDLE_PAUSE_HARD_CAP_MS.
  //
  // Cap is a fail-safe against stuck pause keys, not a scheduling feature.
  // Eviction does not re-trigger usePauseIdleTimer; after force-resume the user
  // gets the full 15-min idle window + 2-min warning (worst case ~45 min protected).
  // To re-pause, toggle shouldPause false→true.
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      let evicted = false;
      pauseMapRef.current.forEach((ts, key) => {
        if (now - ts > IDLE_PAUSE_HARD_CAP_MS) {
          pauseMapRef.current.delete(key);
          evicted = true;
        }
      });
      if (evicted) {
        setIsPaused(pauseMapRef.current.size > 0);
      }
    }, 30_000); // 30 s poll — well under the 30 min cap
    return () => clearInterval(interval);
  }, []);

  return (
    <IdleLogoutContext.Provider value={{ pauseTimer, resumeTimer, isPaused }}>
      {children}
    </IdleLogoutContext.Provider>
  );
};

export const useIdleLogoutContext = (): IdleLogoutContextValue =>
  useContext(IdleLogoutContext);

/**
 * Pause the idle logout timer for the lifetime of the calling component
 * (or while `shouldPause` is true).
 *
 * The useEffect cleanup **always** calls resumeTimer, so unmounting the
 * component guarantees the pause is released even if the caller never
 * flips shouldPause back to false.
 *
 * @param key       Unique identifier for this pause request. Use a
 *                  descriptive constant so concurrent requests don't
 *                  cancel each other.
 * @param shouldPause  Whether to register the pause right now.
 *                     Defaults to true (pause on mount).
 */
export const usePauseIdleTimer = (
  key: string,
  shouldPause: boolean = true,
): void => {
  const { pauseTimer, resumeTimer } = useIdleLogoutContext();

  useEffect(() => {
    if (!shouldPause) return;
    pauseTimer(key);
    // Cleanup always runs on unmount OR when shouldPause flips to false
    return () => {
      resumeTimer(key);
    };
  }, [shouldPause, key, pauseTimer, resumeTimer]);
};
